import React from "react";
import {useTranslation} from "react-i18next";
import {
  BITRATE_480P,
  BITRATE_720P,
  ECameraName,
  EVideoResolution,
  ICameraStreamSettings,
  VideoSettingsWrapper,
} from "../../../interfaces";
import {FieldErrors, get, UseFormRegister} from "react-hook-form";

interface CameraFormProps {
  settings: ICameraStreamSettings;
  register: UseFormRegister<any>;
  errors: FieldErrors<VideoSettingsWrapper>;
  namePrefix: string;
  cameraVendor: ECameraName;
  isMainStream: boolean;
}

const CameraForm: React.FC<CameraFormProps> = ({
  settings,
  register,
  errors,
  namePrefix,
  cameraVendor,
  isMainStream
}) => {
  const { t } = useTranslation();
  const errorResolution = get(errors, `${namePrefix}.resolution`) as
    | { message?: string }
    | undefined;
  const errorBitrate = get(errors, `${namePrefix}.bitrate`) as
    | { message?: string }
    | undefined;
  const errorFps = get(errors, `${namePrefix}.fps`) as
    | { message?: string }
    | undefined;
  const errorKeyframe = get(errors, `${namePrefix}.keyframe`) as
    | { message?: string }
    | undefined;

  const bitrateOptions = isMainStream ? BITRATE_720P : BITRATE_480P;

  return (
    <>
      <div className="input-wrapper">
        <input
            {...register(`${namePrefix}.isMainStr`)}
            type="hidden"
            value={settings.isMain ? 1: 0}
        />
        <label>{t("cameraForm.labels.resolution")}</label>
        <select
            defaultValue={settings.resolution}
          {...register(`${namePrefix}.resolution`, {
            required: t("cameraForm.errors.resolution"),
          })}
        >
          <option value="">{t("cameraForm.placeholders.resolution")}</option>
          {isMainStream && <option value={EVideoResolution.FULL_HD}>1920×1080 (Full HD)</option>}
          {isMainStream && cameraVendor == ECameraName.Dahua && <option value={EVideoResolution.HD_READY}>1280×720 (HD)</option>}
          {!isMainStream && cameraVendor == ECameraName.Dahua && <option value={EVideoResolution.VGA}>640×480 (VGA)</option>}
          {!isMainStream && cameraVendor == ECameraName.Uniview && <option value={EVideoResolution.SD}>640×360 (SD)</option>}
          {!isMainStream && cameraVendor == ECameraName.Dahua && <option value={EVideoResolution.CIF}>352×288 (CIF)</option>}
        </select>
        {errorResolution && (
          <span className="error">{errorResolution.message}</span>
        )}
      </div>

      <div className="input-wrapper">
        <label>{t("cameraForm.labels.bitrate")}</label>
        <select
          defaultValue={settings.bitrate}
          {...register(`${namePrefix}.bitrate`, {
            required: t("cameraForm.errors.bitrate"),
            valueAsNumber: true,
          })}
        >
          <option value="">{t("cameraForm.placeholders.bitrate")}</option>

          {bitrateOptions.map((value) => (
              ((cameraVendor == ECameraName.Dahua &&
              ((value >= 1024 && settings.fps <= 15 && settings.resolution == EVideoResolution.FULL_HD) ||
              (value >= 1280) ||
              (settings.resolution == EVideoResolution.VGA || settings.resolution == EVideoResolution.CIF) ||
              (value >= 768 && settings.fps > 15 && settings.resolution == EVideoResolution.HD_READY) ||
              (settings.fps <= 15 && settings.resolution == EVideoResolution.HD_READY))) ||
              cameraVendor == ECameraName.Uniview) &&
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        {errorBitrate && <span className="error">{errorBitrate.message}</span>}
      </div>

      <div className="input-wrapper">
        <label>{t("cameraForm.labels.fps")}</label>
        <select
          defaultValue={settings.fps}
          {...register(`${namePrefix}.fps`, {
            required: t("cameraForm.errors.fps"),
            valueAsNumber: true,
          })}
        >
          <option value="">{t("cameraForm.placeholders.fps")}</option>
          {Array.from({ length: 25 }, (_, i) => i + 1).map((fps) => (
            <option key={fps} value={fps}>
              {fps}
            </option>
          ))}
        </select>
        {errorFps && <span className="error">{errorFps.message}</span>}
      </div>

      <div className="input-wrapper">
        <label>{t("cameraForm.labels.keyframe")}</label>
        <input
          type="number"
          min={1}
          step={1}
          defaultValue={settings.keyFrameFrequency}
          {...register(`${namePrefix}.keyFrameFrequency`, {
            required: t("cameraForm.errors.keyframe"),
            valueAsNumber: true,
            min: {
              value: 1,
              message: t("cameraForm.errors.keyframeMin"),
            },
            validate: (value) => Number.isInteger(value) || t("cameraForm.errors.keyframeInteger"),
          })}
        />
        {errorKeyframe && (
          <span className="error">{errorKeyframe.message}</span>
        )}
      </div>
    </>
  );
};
export default CameraForm;